import { cva } from "class-variance-authority";
import { CheckIcon } from "lucide-react";
import { type PointerEvent, useCallback } from "react";
import { cn } from "../../utils";
import { useDropdownContext } from "../Dropdown";
import type { SelectOption } from "../Select/types";
import { TruncatedContent } from "../TruncatedContent";
import { useComboBoxContext } from "./context";

const comboBoxItemVariants = cva(
  "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-lg p-2 outline-none text-start",
  {
    variants: {
      disabled: {
        true: "text-neutral-200 cursor-not-allowed",
        false: "hover:bg-primary-500/5 active:bg-primary-500/10",
      },
      active: {
        true: "bg-primary-500/5",
      },
    },
    defaultVariants: {
      disabled: false,
      active: false,
    },
  },
);

type ComboBoxItemProps = {
  option: SelectOption;
  index: number;
};

export const ComboBoxItem = ({ option, index }: ComboBoxItemProps) => {
  const {
    value,
    onRenderOption,
    onSelect,
    contentId,
    multiple,
    descriptionPlacement = "inline",
  } = useComboBoxContext();
  const { navigationRef, activeIndex, setOpen } = useDropdownContext();

  const optionId = `${contentId}-option-${index}`;

  const isSelected = multiple
    ? (Array.isArray(value) ? value : []).some((v) => v.id === option.id)
    : (value as SelectOption)?.id === option.id;

  const isActive = activeIndex === index;

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      if (option.disabled) return;

      onSelect(option);
    },
    [option, onSelect, setOpen, multiple],
  );

  return (
    <div
      {...(option.dataAttributes || {})}
      id={optionId}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      className={comboBoxItemVariants({
        disabled: !!option.disabled,
        active: !option.disabled && isActive,
        className: option.className,
      })}
      ref={navigationRef(index)}
      onPointerDown={handlePointerDown}
      tabIndex={-1}
      data-selected={isSelected ? "" : undefined}
      data-highlighted={isActive ? "" : undefined}
    >
      {onRenderOption ? (
        onRenderOption(option)
      ) : (
        <>
          {option.icon && <div className="flex-shrink-0">{option.icon}</div>}
          <div
            className={cn("flex min-w-0", {
              "flex-col": descriptionPlacement === "stacked",
            })}
          >
            <TruncatedContent className="text-sm whitespace-nowrap">
              {option.text}
            </TruncatedContent>
            {option.description && (
              <TruncatedContent className="text-xs whitespace-nowrap text-neutral-500/50">
                {option.description}
              </TruncatedContent>
            )}
          </div>
        </>
      )}

      {isSelected && (
        <CheckIcon className="ms-auto pointer-events-none shrink-0" size={16} />
      )}
    </div>
  );
};
